import { useCallback, useEffect, useState } from "react";
import { CreditCard, RefreshCw, Truck } from "lucide-react";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";
import { usd } from "@/pages/store/Store";

const STATUS = {
  submitted_for_invoice: ["Awaiting invoice", "#F5A623"], invoiced: ["Invoice sent — pay to ship", "#F5A623"], pending: ["Pending", "#F5A623"],
  paid: ["Paid", "var(--accent)"], shipped: ["Shipped", "#3B82F6"], delivered: ["Delivered", "var(--accent)"],
  canceled: ["Canceled", "#888"], voided: ["Voided", "#888"], refunded: ["Refunded", "#888"], deleted: ["Deleted", "#888"],
};

export default function StoreOrders() {
  const [orders, setOrders] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => api("/store/orders/me").then(setOrders).catch((e) => setErr(e.message)), []);
  const sync = useCallback(async (manual) => {
    if (manual) { setSyncing(true); setMsg(null); }
    try {
      const r = await api("/quickbooks/invoices/sync-mine", { method: "POST" });
      if (r.newly_paid > 0) await load();
      if (manual) setMsg(r.newly_paid > 0 ? `${r.newly_paid} order${r.newly_paid === 1 ? "" : "s"} marked paid.` : r.checked > 0 ? `Checked ${r.checked} invoice${r.checked === 1 ? "" : "s"} · no new payments yet.` : "No open invoices to check.");
    } catch (e) { if (manual) setMsg(e.message); } finally { setSyncing(false); }
  }, [load]);
  useEffect(() => { load(); sync(false); }, [load, sync]);

  return (
    <PortalPage eyebrow="ALR STORE" title="My orders" back="/store" backLabel="Store" testId="orders-page"
      actions={<button onClick={() => sync(true)} disabled={syncing} data-testid="sync-payments-btn" className="flex h-11 items-center gap-2 rounded-xl border border-[#333] bg-[#1A1A1A] px-3 text-xs font-bold"><RefreshCw size={16} className={syncing ? "animate-spin" : ""} /> Check payments</button>}>
      {msg && <p className="text-sm text-neutral-300" data-testid="sync-msg">{msg}</p>}
      <ErrorNote>{err}</ErrorNote>
      {!orders && !err && <Spinner />}
      {orders?.length === 0 && <p className="text-sm text-neutral-400" data-testid="orders-empty">No orders yet.</p>}
      {orders?.filter((o) => o.status !== "deleted").map((o) => {
        const [label, color] = STATUS[o.status] || [o.status, "#888"];
        const unpaid = ["invoiced", "submitted_for_invoice", "pending"].includes(o.status);
        return (
          <Card key={o.id} data-testid={`order-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div><p className="font-mono text-sm font-bold">#{o.qbo_invoice_doc_number || o.id.slice(0, 8)}</p><p className="text-xs text-neutral-500">{fmtDate(o.created_at)}</p></div>
              <span className="rounded-full px-3 py-1 text-[11px] font-extrabold" style={{ color, border: `1px solid ${color}` }} data-testid={`order-status-${o.id}`}>{label}</span>
            </div>
            <ul className="mt-3 space-y-1 text-sm">{o.items.map((it) => <li key={it.product_id} className="flex justify-between gap-3"><span className="text-neutral-300">{it.quantity} × {it.name}</span><span>{usd(it.unit_price_usd * it.quantity)}</span></li>)}</ul>
            <div className="mt-3 flex justify-between border-t border-[#2a2a2a] pt-3 text-sm"><span className="text-neutral-400">Total</span><b>{usd(o.total_usd)}</b></div>
            {o.tracking_number && <p className="mt-2 flex items-center gap-2 text-sm"><Truck size={14} className="text-[var(--accent)]" /> Tracking: <span className="font-mono">{o.tracking_number}</span></p>}
            {o.admin_note && <p className="mt-2 text-xs text-neutral-400">Note from ALR: {o.admin_note}</p>}
            {unpaid && o.qbo_invoice_link && <Btn className="mt-3" onClick={() => window.open(o.qbo_invoice_link, "_blank")} data-testid={`order-pay-${o.id}`}><CreditCard size={16} /> Pay invoice online</Btn>}
          </Card>
        );
      })}
    </PortalPage>
  );
}
